import React from 'react';

/**
 * PhaseDurationBar - Visual time allocation indicator for Play-Practice-Play plan
 *
 * Shows a horizontal bar divided into three colored segments representing
 * the time allocation for each phase. Segments resize based on actual
 * activity durations vs. target durations.
 */
const PhaseDurationBar = ({ phases, totalDuration = 90 }) => {
  if (!phases) return null;

  // Calculate actual durations from activities
  const getDuration = (phaseKey) => {
    const phase = phases[phaseKey];
    if (!phase?.activities) return phase?.target_duration || 0;
    return phase.activities.reduce((sum, act) => sum + (act.duration_minutes || 0), 0);
  };

  const play1Duration = getDuration('play_1');
  const practiceDuration = getDuration('practice');
  const play2Duration = getDuration('play_2');
  const actualTotal = play1Duration + practiceDuration + play2Duration;

  // Calculate percentages based on actual durations
  const getPercent = (duration) => {
    if (actualTotal === 0) return 0;
    return (duration / actualTotal) * 100;
  };

  // Target percentages (for reference markers)
  const targetPlay1 = 20;
  const targetPractice = 50;

  // Phase colors
  const colors = {
    play_1: 'var(--lrbrain-accent)',
    practice: 'var(--lrbrain-navy)',
    play_2: '#10b981'
  };

  return (
    <div className="sp-duration-bar-container">
      {/* Labels */}
      <div className="sp-duration-bar__labels">
        <div className="sp-duration-bar__label" style={{ width: `${getPercent(play1Duration)}%` }}>
          <span className="sp-duration-bar__label-text">Play 1</span>
          <span className="sp-duration-bar__label-time">{play1Duration}m</span>
        </div>
        <div className="sp-duration-bar__label" style={{ width: `${getPercent(practiceDuration)}%` }}>
          <span className="sp-duration-bar__label-text">Practice</span>
          <span className="sp-duration-bar__label-time">{practiceDuration}m</span>
        </div>
        <div className="sp-duration-bar__label" style={{ width: `${getPercent(play2Duration)}%` }}>
          <span className="sp-duration-bar__label-text">Play 2</span>
          <span className="sp-duration-bar__label-time">{play2Duration}m</span>
        </div>
      </div>

      {/* Bar */}
      <div className="sp-duration-bar">
        <div
          className="sp-duration-bar__segment sp-duration-bar__segment--play-1"
          style={{
            width: `${getPercent(play1Duration)}%`,
            backgroundColor: colors.play_1
          }}
        />
        <div
          className="sp-duration-bar__segment sp-duration-bar__segment--practice"
          style={{
            width: `${getPercent(practiceDuration)}%`,
            backgroundColor: colors.practice
          }}
        />
        <div
          className="sp-duration-bar__segment sp-duration-bar__segment--play-2"
          style={{
            width: `${getPercent(play2Duration)}%`,
            backgroundColor: colors.play_2
          }}
        />
      </div>

      {/* Target markers */}
      <div className="sp-duration-bar__targets">
        <div
          className="sp-duration-bar__target-marker"
          style={{ left: `${(targetPlay1 / 90) * 100}%` }}
          title="Target: Play 1 ends at 20 min"
        />
        <div
          className="sp-duration-bar__target-marker"
          style={{ left: `${((targetPlay1 + targetPractice) / 90) * 100}%` }}
          title="Target: Practice ends at 70 min"
        />
      </div>

      {/* Total */}
      <div className="sp-duration-bar__total">
        <span className={`sp-duration-bar__total-value ${actualTotal !== totalDuration ? 'sp-duration-bar__total-value--off' : ''}`}>
          Total: {actualTotal} / {totalDuration} min
          {actualTotal !== totalDuration && (
            <span className="sp-duration-bar__total-diff">
              ({actualTotal > totalDuration ? '+' : ''}{actualTotal - totalDuration})
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default PhaseDurationBar;
